import {
  REGISTRATION_DEFAULT_EXPRESS_TITLE,
  normalizeRegistrationEventCode,
} from "./registrationEventCampaign";

export type RegistrationUrlParams = {
  mode: string | null;
  isExpressMode: boolean;
  isExpressReturnMode: boolean;
  quickCode: string;
  referralCode: string | null;
  referrerContactId: string | null;
  referralQuickCode: string | null;
  eventCode: string;
  eventTitle: string;
  promptPassword: boolean;
  skipQuickCodePrompt: boolean;
  approvalCode: string;
  version: string | null;
  build: string | null;
};

export type ParsedReferralCode = {
  referrerContactId: string | null;
  quickCodeComputed: string | null;
};

function singleValue(value: string | string[] | null | undefined): string {
  if (Array.isArray(value)) return String(value[0] ?? "");
  return String(value ?? "");
}

function isTruthy(raw: string): boolean {
  return ["1", "true", "yes", "y"].includes(raw.trim().toLowerCase());
}

function normalizeEventTitle(raw: string): string {
  return raw.trim().replace(/\s+/g, " ");
}

export function parseReferralCodePayload(referralCode: string): ParsedReferralCode | null {
  try {
    const decoded = atob(referralCode.trim());
    const parsed = JSON.parse(decoded) as {
      referrerContactId?: string;
      quickCodeComputed?: string;
    };
    return {
      referrerContactId: parsed.referrerContactId?.trim() || null,
      quickCodeComputed: parsed.quickCodeComputed
        ? String(parsed.quickCodeComputed).trim().toUpperCase()
        : null,
    };
  } catch {
    return null;
  }
}

/** Parse registration URL search string (parity with buxusweb `Register` mount). */
export function parseRegistrationUrlParams(search: string): RegistrationUrlParams {
  const q = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const get = (key: string): string => singleValue(q.get(key));

  const mode = get("mode").toLowerCase();
  const isExpressMode = mode === "express";
  const directQuickCode = get("quickCode").trim().toUpperCase();
  const referralRaw = get("referralCode").trim();

  let referrerContactId: string | null = null;
  let referralQuickCode: string | null = null;
  if (referralRaw) {
    const parsed = parseReferralCodePayload(referralRaw);
    referrerContactId = parsed?.referrerContactId ?? null;
    referralQuickCode = parsed?.quickCodeComputed ?? null;
  }

  const expressResumeQuickCode = (directQuickCode || referralQuickCode || "").trim();
  const isExpressReturnMode = isExpressMode && !!expressResumeQuickCode;

  const eventCodeRaw = get("eventCode");
  const eventTitleFromUrl = normalizeEventTitle(get("eventTitle"));

  return {
    mode: mode || null,
    isExpressMode,
    isExpressReturnMode,
    quickCode: expressResumeQuickCode || directQuickCode,
    referralCode: referralRaw || null,
    referrerContactId,
    referralQuickCode,
    eventCode: normalizeRegistrationEventCode(eventCodeRaw),
    eventTitle: eventTitleFromUrl || (isExpressMode ? REGISTRATION_DEFAULT_EXPRESS_TITLE : ""),
    promptPassword: isExpressMode && isTruthy(get("promptPassword")),
    skipQuickCodePrompt: !isExpressMode && isTruthy(get("skipQuickCodePrompt")),
    approvalCode: get("approvalCode").trim(),
    version: get("version").trim() || null,
    build: get("build").trim() || null,
  };
}
